import { Component, OnInit, inject, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import {
  Campaign, CampaignAdminRequest, OpenerStrategy,
  CampaignLocationFilter, CampaignsCampaignAdminApiClient,
  CampaignsCampaignLocationFilterAdminApiClient,
  CampaignsOutboundEventConfigurationApiClient,
  SchemeLocationFilter
} from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { OpenerSchemeListComponent } from './opener-scheme-list/opener-scheme-list.component';
import { BannerImage } from '../_/campaign-banner-image/campaign-banner-image.component';
import { CampaignBannerComponent } from '../_/campaign-banner/campaign-banner.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PERMISSION } from 'src/app/const/permission';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { MatCardModule } from '@angular/material/card';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { CampaignOutboundEventsComponent } from '../campaign-outbound-events/campaign-outbound-events.component';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { MatIconModule } from '@angular/material/icon';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTabsModule } from '@angular/material/tabs';
import { CampaignCloneService } from 'src/app/common/services/campaign-clone.service';

@Component({
  selector: 'app-opener-campaign-edit',
  templateUrl: './opener-campaign-edit.component.html',
  styleUrls: ['./opener-campaign-edit.component.scss'],
  imports: [
    CampaignBaseEditComponent,
    CampaignBannerComponent,
    OpenerSchemeListComponent,
    CampaignLocationFiltersComponent,
    CampaignMediaComponent,
    CampaignOutboundEventsComponent,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    FlexLayoutModule,
    JsonPipe,
    AsyncPipe,
    HasAnyPermissionDirective,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
]
})
export class OpenerCampaignEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private outboundEventsApi = inject(CampaignsOutboundEventConfigurationApiClient);
  private campaignCloneService = inject(CampaignCloneService);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);


  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignBanner = viewChild<CampaignBannerComponent>('campaignBanner');
  readonly campaignSchemeList = viewChild<OpenerSchemeListComponent>('campaignSchemeList');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');
  readonly campaignOutboundEvents = viewChild<CampaignOutboundEventsComponent>('campaignOutboundEvents');

  editModel: CampaignAdminRequest;
  campaign: Campaign;
  campaignReplay = new BehaviorSubject<Campaign>(undefined);
  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  clonedLocationFilters: CampaignLocationFilter;
  clonedSchemeLocationFilters: SchemeLocationFilter[] = [];
  isLoading: boolean;
  isAdding: boolean;
  isCloning: boolean;
  viewMode: 'scheme' | 'locations' | 'media' | 'banner' | 'json' | '' = 'scheme';
  allowFlow: boolean;     // triggerd from Tiers tab to update validators on depending tabs
  readonly permissions = PERMISSION;

  onAllowFlowChange(allowFlow: boolean) {
    this.allowFlow = allowFlow;
  }

  constructor() {
    this.isAdding = this.route.snapshot.params.campaignId === 'new';
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
    this.originalCampaignId = this.campaignId;
  }

  async ngOnInit() {
    try {
      this.isLoading = true;
      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({ advertiserId: this.advertiserId });
      }
    } catch (err: any) {
    } finally {
      setTimeout(() => {
        this.isLoading = false;
      }, 1500);
    }

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);

    setTimeout(() => {
      this.syncBanners();
    }, 500);
  }

  syncBanners() {
    // sync baseCampaign main banner with banner slider tab (can be edited from 2 places)
    this.campaignBase().mainBannerChanged.subscribe(banner => { this.campaignBanner().setMainBanner(banner); });
    this.campaignBanner().mainBannerChanged.subscribe(banner => { this.campaignBase().setMainBanner(banner); });

    if (this.campaign.strategy)
      this.campaignBanner().setBanners(this.campaign.strategy.banner);

    if (!this.campaign.strategy || !this.campaign.strategy.banner) {
      // old campaings have banner set on campaign.photoBannerUrl prop -> add it to the banner list
      if (this.campaign.photoBannerUrl)
        this.campaignBanner().setMainBanner(new BannerImage({ photoUrl: this.campaign.photoBannerUrl }));
    }
    else {
      // new campaign will have banner list -> set it also on the campaign base main banner
      const strategyBanner = this.campaign.strategy.banner;
      if (strategyBanner && strategyBanner.url1)
        this.campaignBase().setMainBanner(new BannerImage({ photoUrl: strategyBanner.url1 }))
    }
  }

  async onSave() {
    this.campaignOutboundEvents()?.markAsTouched();

    const request = this.campaignBase().getFormValue();
    if (!request) return;

    if (!this.campaignOutboundEvents().isValid()) return;

    const strategy = new OpenerStrategy({ name: 'OpenerStrategy' });

    request.campaignLocationFilter = this.campaignLocationFilters().getFormValue();  // update model with tab locations
    strategy.schemes = [];
    request.schemeLocationFilters = [];
    this.campaignSchemeList().schemeIds.forEach(x => {
      const campaignSchemeList = this.campaignSchemeList();
      strategy.schemes.push(campaignSchemeList.savedSchemes[x]);
      request.schemeLocationFilters.push(campaignSchemeList.savedLocationFilters[x]);
    });

    const outOfBoundSchemes = strategy.schemes.filter(x => x.startDate < request.startDate || x.endDate > request.endDate);
    if (outOfBoundSchemes && outOfBoundSchemes.length > 0) {
      outOfBoundSchemes.forEach(x => new MessageDialogModel(`The start date or end date for scheme ${x.name} is out of bounds.`).show(this.dialog));
      return;
    }

    request.campaignId = this.campaignId;
    request.advertiserId = this.advertiserId;
    request.strategy = strategy;
    request.strategy.banner = this.campaignBanner().getBanners();

    this.isLoading = true;
    try {
      const savedCampaign = await lastValueFrom(this.campaignsApi.createCampaign(request));

      const outboundEventsConfig = this.campaignOutboundEvents()?.getFormValue();
      if (outboundEventsConfig && savedCampaign?.campaignId) {
        try {
          await lastValueFrom(this.outboundEventsApi.updateCampaignConfig(savedCampaign.campaignId, outboundEventsConfig));
        } catch (err) {
          console.warn('Failed to save outbound events configuration:', err);
        }
      }

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);
    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    } finally {
      this.isLoading = false;
    }
  }

  private async cloneCampaign() {
    const { campaign, locationFilters, schemeLocationFilters } = await this.campaignCloneService.cloneCampaign(this.originalCampaignId, this.advertiserId);

    this.campaign = campaign;
    this.isAdding = true;
    this.campaignId = undefined;
    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = locationFilters;
    // Store scheme location filters - already ordered by scheme position
    this.clonedSchemeLocationFilters = schemeLocationFilters || [];
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (const key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (const key of Object.keys(obj))
      ref[key] = obj[key];
  }
}
